// ============================================================================
// THÈME WEB — monte le vocabulaire canonique (`ui-tokens/theme-web`) dans le DOM et gère la bascule
// clair/sombre persistée (§24.9sexies-ter d : localStorage + `data-theme` sur <html>).
//
// ⚠️ `<ThemeVars/>` se rend DANS le corps du document, donc APRÈS la feuille de l'app dans l'ordre de
// cascade : à spécificité égale, les variables canoniques gagnent — l'app n'a pas à « penser » à
// l'ordre de ses imports. Les feuilles locales consomment `var(--…)`, ne redéfinissent jamais la charte.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { webThemeCssText, type WebThemeOverrides } from '@orgazone/ui-tokens';

export interface ThemeVarsProps {
  /** Mode que `:root` porte AVANT que le hook ait posé `data-theme` (évite un flash au chargement). */
  defaultMode?: 'light' | 'dark';
  /** Personnalité de l'app : palette de marque, rail, polices (cf. `WebThemeOverrides`). */
  overrides?: WebThemeOverrides;
}

/** Monte la charte (variables CSS clair + sombre) — UNE fois, près de la racine. */
export function ThemeVars({ defaultMode = 'light', overrides }: ThemeVarsProps) {
  const css = useMemo(() => webThemeCssText({ defaultMode, overrides }), [defaultMode, overrides]);
  return <style>{css}</style>;
}

/**
 * Bascule de thème persistée — la clé de stockage reste PAR APP (préférence locale au poste),
 * seule la mécanique est partagée. Pose `data-theme` sur <html> ; les blocs de `ThemeVars` suivent.
 */
export function useWebTheme(storageKey: string, defaultMode: 'light' | 'dark' = 'light') {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = typeof localStorage === 'undefined' ? null : localStorage.getItem(storageKey);
    return saved === 'light' || saved === 'dark' ? saved : defaultMode;
  });
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    localStorage.setItem(storageKey, mode);
  }, [mode, storageKey]);
  // ⚠️ Référence STABLE : `toggle` entre dans des useMemo/props mémoïsés côté apps — une fonction
  // recréée à chaque rendu les invaliderait tous (et eslint exhaustive-deps le signale, à raison).
  const toggle = useCallback(() => setMode((m) => (m === 'dark' ? 'light' : 'dark')), []);
  return { mode, toggle, setMode };
}
